  import React, { useMemo } from "react";
  import {
    MaterialReactTable,
    MRT_ShowHideColumnsButton as MrtShowHideColumnsButton,
    MRT_ToggleDensePaddingButton as MrtToggleDensePaddingButton,
    MRT_ToggleFiltersButton as MrtToggleFiltersButton,
    MRT_ToggleFullScreenButton as MrtToggleFullScreenButton,
  } from "material-react-table";
  import {
    Box,
    IconButton,
    InputAdornment,
    TextField,
    Tooltip,
    Typography,
  } from "@mui/material";
  import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
  import { FaSort, FaSortDown, FaSortUp } from "react-icons/fa6";

  const headerNoWrapSx = {
    display: "block",
    lineHeight: "16px",
    maxWidth: "100%",
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    wordBreak: "keep-all",
  };

  const normalizedHeaderSx = {
    fontSize: "12px",
    backgroundColor: "#fbfcfd",
    color: "#242627 !important",
    fontWeight: 700,
    padding: "0 0.4rem",
    paddingLeft: "0.4rem",
    paddingRight: "0.4rem",
    height: "42px",
    minHeight: "42px",
    maxHeight: "42px",
    boxSizing: "border-box",
    verticalAlign: "middle",
    textAlign: "left",
    whiteSpace: "nowrap",
    overflow: "hidden",
    borderBottom: "1px solid #e4e7eb",
    borderRight: "none",
    borderTop: "none",
    position: "sticky",
    top: 0,
    zIndex: 2,
    "& *": {
      boxSizing: "border-box",
      whiteSpace: "nowrap !important",
    },
    "& .Mui-TableHeadCell-Content": {
      alignItems: "center",
      gap: "4px",
      height: "42px",
      minHeight: "42px",
      maxHeight: "42px",
      justifyContent: "flex-start",
      overflow: "hidden",
      width: "100%",
      whiteSpace: "nowrap",
      flexWrap: "nowrap",
    },
    "& .Mui-TableHeadCell-Content > *": {
      minWidth: 0,
    },
    "& [class*='Mui-TableHeadCell-Content']": {
      minHeight: "0 !important",
      maxHeight: "42px",
      overflow: "hidden",
      whiteSpace: "nowrap !important",
      flexWrap: "nowrap !important",
    },
    "& .Mui-TableHeadCell-Content-Labels": {
      alignItems: "center",
      display: "flex",
      gap: "4px",
      flex: "0 1 auto",
      flexWrap: "nowrap",
      minWidth: 0,
      overflow: "hidden",
      whiteSpace: "nowrap",
    },
    "& .Mui-TableHeadCell-Content-Wrapper": {
      ...headerNoWrapSx,
      flex: "1 1 auto",
    },
    "& .Mui-TableHeadCell-Content-Wrapper *": {
      maxWidth: "100%",
      minWidth: 0,
      whiteSpace: "nowrap !important",
    },
    "& .global-sort-header": {
      alignItems: "center",
      display: "inline-flex !important",
      gap: "4px",
      maxWidth: "100%",
      minWidth: 0,
      overflow: "hidden",
      verticalAlign: "middle",
      whiteSpace: "nowrap !important",
    },
    "& .global-sort-header__label": {
      display: "block",
      minWidth: 0,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap !important",
    },
    "& .global-sort-header__icon": {
      display: "inline-block !important",
      flexShrink: 0,
    },
    "& .MuiTypography-root, & .MuiTableSortLabel-root, & .MuiTableSortLabel-root span":
      headerNoWrapSx,
    // "& .Mui-TableHeadCell-Content-Actions": {
    //   alignItems: "center",
    //   display: "flex",
    //   flexShrink: 0,
    //   gap: "3px",
    //   marginLeft: "6px",
    // },
    // "& .Mui-TableHeadCell-Content-Actions .MuiIconButton-root": {
    //   color: "#c5cbd3",
    //   height: "20px",
    //   padding: "2px",
    //   width: "20px",
    // },
    // "& .Mui-TableHeadCell-Content-Actions .MuiSvgIcon-root": {
    //   fontSize: "15px",
    // },
    "& .MuiTableSortLabel-root": {
      alignItems: "center",
      display: "inline-flex",
      maxWidth: "100%",
      overflow: "hidden",
      whiteSpace: "nowrap",
      "& .MuiTableSortLabel-icon": {
        color: "#c5cbd3 !important",
        fontSize: "14px",
        marginLeft: "4px",
      },
    },
  };

  const globalHeadCellSx = {
    ...normalizedHeaderSx,
    "& .Mui-TableHeadCell-Content": {
      ...normalizedHeaderSx["& .Mui-TableHeadCell-Content"],
      justifyContent: "flex-start",
    },
    "& .Mui-TableHeadCell-Content-Labels": {
      ...normalizedHeaderSx["& .Mui-TableHeadCell-Content-Labels"],
      flex: "0 1 auto",
    },
    // "& .Mui-TableHeadCell-Content-Actions": {
    //   ...normalizedHeaderSx["& .Mui-TableHeadCell-Content-Actions"],
    //   visibility: "visible",
    // },
    // "& .Mui-TableHeadCell-Content-Actions .MuiIconButton-root": {
    //   ...normalizedHeaderSx[
    //     "& .Mui-TableHeadCell-Content-Actions .MuiIconButton-root"
    //   ],
    //   opacity: 1,
    // },
    "& .MuiButtonBase-root.MuiIconButton-root": {
      color: "#c5cbd3",
    },
    "& .MuiButtonBase-root.MuiIconButton-root:hover": {
      backgroundColor: "transparent",
      color: "#7b8490",
    },
    "& .MuiTableSortLabel-icon": {
      display: "none",
    },
  };

  export const dashboardTableProps = {
    enableColumnActions: false,
    enableGlobalFilter: true,
    positionGlobalFilter: "left",
    className: "p-datatable-striped custom-data-table px-3",
    initialState: {
      showGlobalFilter: false,
    },
    renderTopToolbarCustomActions: ({ table }) => {
      const value = table.getState().globalFilter ?? "";

      return (
        <TextField
          size="small"
          placeholder="Search"
          value={value}
          onChange={(event) =>
            table.setGlobalFilter(event.target.value || undefined)
          }
          InputProps={{
            endAdornment: value ? (
              <InputAdornment position="end">
                <IconButton
                  aria-label="Clear search"
                  size="small"
                  onClick={() => table.setGlobalFilter(undefined)}
                  edge="end"
                >
                  <CloseRoundedIcon />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
          sx={{
            width: 190,
            "& .MuiOutlinedInput-root": {
              bgcolor: "#FFFFFF",
              borderRadius: "8px",
              fontSize: "12.5px",
              height: 36,
              "& fieldset": {
                borderColor: "#DDE3EA",
              },
              "&:hover fieldset": {
                borderColor: "#CBD5E1",
              },
              "&.Mui-focused fieldset": {
                borderColor: "#93C5FD",
                borderWidth: "1px",
              },
            },
            "& .MuiInputBase-input": {
              py: 0,
              "&::placeholder": {
                color: "#94A3B8",
                opacity: 1,
              },
            },
            "& .MuiInputAdornment-root": {
              mr: -0.5,
            },
            "& .MuiIconButton-root": {
              color: "#94A3B8",
              height: 26,
              p: 0.25,
              width: 26,
            },
            "& .MuiIconButton-root:hover": {
              bgcolor: "transparent",
              color: "#64748B",
            },
            "& .MuiSvgIcon-root": {
              fontSize: 18,
            },
          }}
        />
      );
    },
    renderToolbarInternalActions: ({ table }) => (
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.2, p: 0 }}>
        <MrtToggleFiltersButton
          table={table}
          sx={{
            width: 30,
            height: 30,
            p: "6px",
            borderRadius: "8px",
            color: "#334155",
            "&:hover": { bgcolor: "#EFF6FF", color: "#2563EB" },
            "& .MuiSvgIcon-root": { fontSize: 18 },
          }}
        />
        <MrtShowHideColumnsButton
          table={table}
          sx={{
            width: 30,
            height: 30,
            p: "6px",
            borderRadius: "8px",
            color: "#334155",
            "&:hover": { bgcolor: "#EFF6FF", color: "#2563EB" },
            "& .MuiSvgIcon-root": { fontSize: 18 },
          }}
        />
        <MrtToggleDensePaddingButton
          table={table}
          sx={{
            width: 30,
            height: 30,
            p: "6px",
            borderRadius: "8px",
            color: "#334155",
            "&:hover": { bgcolor: "#EFF6FF", color: "#2563EB" },
            "& .MuiSvgIcon-root": { fontSize: 18 },
          }}
        />
        <Tooltip title={table.getState().isFullScreen ? "Exit Full Screen" : "Full Screen"} arrow>
          <span>
            <MrtToggleFullScreenButton
              title=""
              table={table}
              sx={{
                width: 30,
                height: 30,
                p: "6px",
                borderRadius: "8px",
                color: "#334155",
                "&:hover": { bgcolor: "#EFF6FF", color: "#2563EB" },
                "& .MuiSvgIcon-root": { fontSize: 18 },
              }}
            />
          </span>
        </Tooltip>
      </Box>
    ),
    muiTablePaperProps: {
      sx: {
        border: "1px solid #E2E8F0",
        borderRadius: "12px",
        bgcolor: "#FFFFFF",
        boxShadow: "0 12px 28px rgba(15, 23, 42, 0.06)",
        overflow: "hidden",
        '&[style*="position: fixed"]': {
          border: "none",
          borderRadius: 0,
          width: "100dvw !important",
          maxWidth: "100dvw !important",
        },
        '&[style*="position: fixed"] .MuiTableContainer-root': {
          width: "100% !important",
          maxWidth: "100% !important",
        },
        '&[style*="position: fixed"] table': {
          width: "100% !important",
        },
      },
    },
    muiTopToolbarProps: {
      sx: {
        alignItems: "center",
        borderBottom: "1px solid #EEF2F7",
        boxShadow: "none",
        minHeight: 58,
        px: 1.25,
        py: 0.9,
        "& .MuiBox-root": {
          alignItems: "center",
        },
        "& .MuiIconButton-root": {
          borderRadius: "8px",
          color: "#334155",
          height: 30,
          p: "6px",
          width: 30,
        },
        "& .MuiIconButton-root:hover": {
          bgcolor: "#EFF6FF",
          color: "#2563EB",
        },
        "& .MuiSvgIcon-root": {
          fontSize: 18,
        },
      },
    },
    muiTableContainerProps: {
      sx: {
        borderTop: "none",
        maxHeight: "520px",
        bgcolor: "#FFFFFF",
        flex: "0 0 auto",
        width: "100%",
        maxWidth: "100%",
        overflowX: "auto",
      },
    },
    muiSearchTextFieldProps: {
      variant: "outlined",
      size: "small",
      placeholder: "Search",
      InputProps: {
        startAdornment: null,
      },
      sx: {
        minWidth: "190px",
        maxWidth: "220px",
        width: "190px",
        "& .MuiOutlinedInput-root": {
          bgcolor: "#FFFFFF",
          borderRadius: "8px",
          fontSize: "12.5px",
          height: 36,
          "& fieldset": {
            borderColor: "#DDE3EA",
          },
          "&:hover fieldset": {
            borderColor: "#CBD5E1",
          },
          "&.Mui-focused fieldset": {
            borderColor: "#93C5FD",
            borderWidth: "1px",
          },
        },
        "& .MuiInputBase-input": {
          py: 0,
          px: 1.4,
          "&::placeholder": {
            color: "#94A3B8",
            opacity: 1,
          },
        },
        "& .MuiInputAdornment-root": {
          mr: -0.5,
        },
        "& .MuiIconButton-root": {
          color: "#94A3B8",
          height: 26,
          p: 0.25,
          width: 26,
        },
        "& .MuiIconButton-root:hover": {
          bgcolor: "transparent",
          color: "#64748B",
        },
        "& .MuiSvgIcon-root": {
          fontSize: 18,
        },
      },
    },
    muiTableProps: {
      sx: {
        tableLayout: "fixed",
        width: "max-content",
        "& thead tr": {
          height: "40px !important",
          maxHeight: "40px !important",
          minHeight: "40px !important",
        },
        "& thead th": {
          paddingTop: "6px !important",
          paddingBottom: "6px !important",
        },
        "& tbody td": {
          paddingLeft: "8px !important",
          paddingRight: "8px !important",
          verticalAlign: "middle !important",
        },
        "& tbody td [class*='TableBodyCell-Content']": {
          alignItems: "center !important",
          minHeight: "0 !important",
        },
        "& thead th:last-of-type, & tbody td:last-of-type": {
          borderRight: "none !important",
        },
      },
    },
    muiTableHeadRowProps: {
      sx: {
        boxShadow: "none !important",
        height: "40px !important",
        maxHeight: "40px !important",
        minHeight: "40px !important",
      },
    },
    muiTableHeadCellProps: {
      sx: {
        bgcolor: "#F8FAFC",
        boxShadow: "none !important",
        borderBottom: "1px solid #EEF0F3",
        borderRight: "none",
        color: "#242627 !important",
        fontSize: "12px",
        fontWeight: 700,
        letterSpacing: 0,
        textTransform: "none",
        verticalAlign: "middle",
        "& .Mui-TableHeadCell-Content": {
          alignItems: "center !important",
          height: "32px !important",
          maxHeight: "32px !important",
          minHeight: "32px !important",
        },
        "& .Mui-TableHeadCell-Content-Labels": {
          alignItems: "center !important",
          height: "32px !important",
          maxHeight: "32px !important",
          minHeight: "32px !important",
        },
      },
    },
    muiTableBodyCellProps: {
      sx: {
        bgcolor: "#FFFFFF",
        borderBottom: "1px solid #EEF0F3",
        borderRight: "none",
        color: "#1F2937",
        fontSize: "12px",
        fontWeight: 500,
        paddingLeft: "8px !important",
        paddingRight: "8px !important",
        verticalAlign: "middle",
        "& .Mui-TableBodyCell-Content, & [class*='TableBodyCell-Content']": {
          alignItems: "center",
          minHeight: "0 !important",
        },
      },
    },
    muiBottomToolbarProps: {
      sx: {
        borderTop: "1px solid #D7DCE2",
        boxShadow: "none",
        minHeight: "42px",
        "& .MuiTablePagination-root, & .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows, & .MuiSelect-select":
          {
            color: "#334155",
            fontFamily: "inherit",
            fontSize: "12px",
            fontWeight: 600,
          },
        "& .MuiIconButton-root": {
          color: "#64748B",
          p: 0.5,
        },
      },
    },
  };

  const SortHeader = ({ column, label }) => {
    const sortDirection = column.getIsSorted?.();
    const canSort = column.getCanSort?.();
    const SortIcon =
      sortDirection === "asc"
        ? FaSortUp
        : sortDirection === "desc"
          ? FaSortDown
          : FaSort;

    return (
      <Box
        component="span"
        className="global-sort-header"
        title={label}
        sx={{
          alignItems: "center",
          display: "inline-flex",
          gap: "4px",
          lineHeight: "16px",
          maxWidth: "100%",
          minWidth: 0,
          overflow: "hidden",
          whiteSpace: "nowrap",
        }}
      >
        <Box
          component="span"
          className="global-sort-header__label"
          sx={{
            display: "block",
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {label}
        </Box>
        {canSort && (
          <SortIcon
            aria-hidden="true"
            className="global-sort-header__icon"
            style={{
              color: "#888",
              flexShrink: 0,
              fontSize: "12px",
              lineHeight: 1,
            }}
          />
        )}
      </Box>
    );
  };

  export const GlobalDataTable = ({
    data = [],
    columns = [],
    error,
    errorMessage,
    pageSize,
    rows,
    first = 0,
    initialState,
    emptyStateAlign = "center",
    onRowClick,
    defaultColumn,
    muiTableProps,
    muiTableHeadRowProps,
    muiTableHeadCellProps,
    muiTableBodyCellProps,
    muiTablePaperProps,
    muiTableContainerProps,
    muiSearchTextFieldProps,
    muiPaginationProps,
    muiColumnVisibilityMenuItemProps,
    muiColumnActionMenuProps,
    muiTableHeadCellFilterButtonProps,
    muiTableHeadCellColumnActionsMenuItemsProps,
    renderEmptyRowsFallback,
    renderTopToolbarCustomActions,
    renderToolbarInternalActions,
    renderColumnActionsMenuItems,
    enableTopToolbar,
    enableToolbarInternalActions,
    enableStickyHeader,
    enableColumnFilters,
    enableGlobalFilter,
    enablePagination,
    enableSorting,
    enableDensityToggle,
    enableFullScreenToggle,
    enableHiding,
    enableColumnActions,
    manualFiltering,
    manualPagination,
    manualSorting,
    filters,
    onFilter,
    globalFilter,
    columnFilters,
    onGlobalFilterChange,
    onColumnFiltersChange,
    paginator,
    paginatorLeft,
    emptyMessage,
    selection,
    onSelectionChange,
    dataKey,
    className,
    style,
    areFiltersEnabled,
    globalFilterFields,
    positionGlobalFilter,
    ...props
  }) => {
    const initialPageSize = pageSize || rows || 5;
    const initialPageIndex = Math.floor((first || 0) / initialPageSize);

    const normalizedColumns = useMemo(() => {
      const normalizeColumn = (column) => {
        const originalHeadCellProps = column.muiTableHeadCellProps;
        const childColumns = column.columns?.map(normalizeColumn);

        return {
          ...column,
          ...(childColumns ? { columns: childColumns } : {}),
          Header:
            column.Header ??
            (({ column: mrtColumn }) => (
              <SortHeader
                column={mrtColumn}
                label={column.header || column.accessorKey || ""}
              />
            )),
          muiTableHeadCellProps: (cellProps) => {
            const resolvedHeadCellProps =
              typeof originalHeadCellProps === "function"
                ? originalHeadCellProps(cellProps)
                : originalHeadCellProps;

            return {
              ...resolvedHeadCellProps,
              sx: {
                ...normalizedHeaderSx,
                ...resolvedHeadCellProps?.sx,
              },
              style: {
                ...resolvedHeadCellProps?.style,
              },
            };
          },
        };
      };

      return columns.map(normalizeColumn);
    }, [columns]);

    const customIcons = {
      SortIcon: (iconProps) => (
        <FaSort
          {...iconProps}
          style={{ fontSize: "11px", marginLeft: "4px" }}
        />
      ),
      SyncAltIcon: (iconProps) => (
        <FaSort
          {...iconProps}
          style={{ fontSize: "11px", marginLeft: "4px" }}
        />
      ),
    };

    return (
      <MaterialReactTable
        {...props}
        columns={normalizedColumns}
        data={data}
        icons={customIcons}
        localization={{ filterByColumn: "Filter by {column}" }}
        enableSorting={enableSorting ?? true}
        enableColumnFilters={enableColumnFilters ?? true}
        enableGlobalFilter={enableGlobalFilter ?? true}
        enablePagination={enablePagination ?? true}
        enableTopToolbar={enableTopToolbar ?? true}
        enableToolbarInternalActions={enableToolbarInternalActions ?? false}
        enableStickyHeader={enableStickyHeader ?? true}
        enableDensityToggle={enableDensityToggle ?? true}
        enableFullScreenToggle={enableFullScreenToggle ?? true}
        enableHiding={enableHiding ?? true}
        enableColumnActions={enableColumnActions ?? true}
        positionGlobalFilter={positionGlobalFilter ?? "left"}
        defaultColumn={{
          minSize: 130,
          size: 170,
          ...defaultColumn,
        }}
        initialState={{
          showGlobalFilter: false,
          density: "compact",
          ...initialState,
          pagination: {
            pageSize: initialPageSize,
            pageIndex: initialState?.pagination?.pageIndex ?? initialPageIndex,
            ...initialState?.pagination,
          },
        }}
        renderColumnActionsMenuItems={
          renderColumnActionsMenuItems ||
          (({ internalColumnMenuItems }) =>
            internalColumnMenuItems.filter(
              (item) =>
                !String(item.props?.label).toLowerCase().includes("sort"),
            ))
        }
        renderTopToolbarCustomActions={renderTopToolbarCustomActions}
        renderToolbarInternalActions={
          renderToolbarInternalActions ||
          (({ table }) => (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <MrtToggleFiltersButton table={table} />
              <MrtShowHideColumnsButton table={table} />
              <MrtToggleDensePaddingButton table={table} />
              <Tooltip title={table.getState().isFullScreen ? "Exit Full Screen" : "Full Screen"} arrow>
                <span><MrtToggleFullScreenButton table={table} title="" /></span>
              </Tooltip>
            </Box>
          ))
        }
        muiTableProps={{
          ...muiTableProps,
          sx: {
            tableLayout: "auto",
            borderCollapse: "separate",
            borderSpacing: 0,
            "& thead th:last-of-type, & tbody td:last-of-type": {
              borderRight: "none",
            },
            ...muiTableProps?.sx,
          },
        }}
        muiTableHeadRowProps={(props) => {
          const density = props?.table?.getState?.()?.density || "compact";
          const headHeight = density === 'compact' ? '42px' : density === 'comfortable' ? '54px' : '66px';

          const customRowProps =
            typeof muiTableHeadRowProps === "function"
              ? muiTableHeadRowProps(props)
              : muiTableHeadRowProps;

          return {
            ...customRowProps,
            sx: {
              height: headHeight,
              minHeight: headHeight,
              maxHeight: headHeight,
              boxShadow: "0 2px 5px rgba(15, 23, 42, 0.08)",
              ...customRowProps?.sx,
            },
          };
        }}
        muiTableHeadCellProps={(cellProps) => {
          const density = cellProps.table?.getState?.()?.density || "compact";
          const headPy = density === 'compact' ? '4px' : density === 'comfortable' ? '12px' : '18px';

          const customHeadProps =
            typeof muiTableHeadCellProps === "function"
              ? muiTableHeadCellProps(cellProps)
              : muiTableHeadCellProps;

          return {
            ...customHeadProps,
            sx: {
              ...globalHeadCellSx,
              paddingLeft: "0.4rem",
              paddingRight: "0.4rem",
              paddingTop: `${headPy} !important`,
              paddingBottom: `${headPy} !important`,
              textAlign: "left",
              ...customHeadProps?.sx,
            },
            style: {
              ...customHeadProps?.style,
            },
          };
        }}
        muiTableBodyCellProps={(cellProps) => {
          const density = cellProps.table?.getState?.()?.density || "compact";
          const dynamicPy = density === 'compact' ? '4px' : density === 'comfortable' ? '12px' : '18px';
          const dynamicHeight = density === 'compact' ? '38px' : density === 'comfortable' ? '54px' : '66px';

          const customBodyProps =
            typeof muiTableBodyCellProps === "function"
              ? muiTableBodyCellProps(cellProps)
              : muiTableBodyCellProps;

          return {
            ...customBodyProps,
            sx: {
              fontSize: "12px",
              paddingLeft: "0.4rem",
              paddingRight: "0.4rem",
              paddingTop: `${dynamicPy} !important`,
              paddingBottom: `${dynamicPy} !important`,
              height: `${dynamicHeight} !important`,
              borderBottom: "1px solid #edefef",
              borderRight: "1px solid #EEF2F7",
              ...customBodyProps?.sx,
            },
            style: {
              ...customBodyProps?.style,
            },
          };
        }}
        muiTableBodyRowProps={(rowProps) => ({
          onClick: onRowClick
            ? (event) => onRowClick(event, rowProps.row.original)
            : undefined,
          sx: {
            cursor: onRowClick ? "pointer" : "default",
          },
        })}
        muiColumnVisibilityMenuItemProps={{
          ...muiColumnVisibilityMenuItemProps,
          sx: {
            fontSize: "12px",
            ...muiColumnVisibilityMenuItemProps?.sx,
          },
        }}
        muiColumnActionMenuProps={{
          ...muiColumnActionMenuProps,
          sx: {
            "& .MuiMenuItem-root": {
              fontSize: "12px",
            },
            ...muiColumnActionMenuProps?.sx,
          },
        }}
        muiTableHeadCellFilterButtonProps={{
          ...muiTableHeadCellFilterButtonProps,
          sx: {
            backgroundColor: "transparent !important",
            boxShadow: "none !important",
            color: "#555 !important",
            borderRadius: "0 !important",
            padding: "0 !important",
            minWidth: "auto",
            ...muiTableHeadCellFilterButtonProps?.sx,
          },
        }}
        muiTableHeadCellColumnActionsMenuItemsProps={{
          ...muiTableHeadCellColumnActionsMenuItemsProps,
          sx: {
            fontSize: "12px !important",
            ...muiTableHeadCellColumnActionsMenuItemsProps?.sx,
          },
        }}
        muiTablePaperProps={{
          elevation: 0,
          ...muiTablePaperProps,
          sx: {
            display: "flex",
            flexDirection: "column",
            flex: 1,
            height: "100%",
            overflow: "hidden",
            border: "1px solid #E2E8F0",
            borderRadius: "12px",
            boxShadow: "none",
            ...muiTablePaperProps?.sx,
          },
        }}
        muiTableContainerProps={{
          ...muiTableContainerProps,
          sx: {
            flex: 1,
            overflow: "auto",
            borderTop: "1px solid #E2E8F0",
            ...muiTableContainerProps?.sx,
          },
        }}
        muiSearchTextFieldProps={{
    ...muiSearchTextFieldProps,
    variant: "outlined",
    size: "small",

    InputProps: {
      ...(muiSearchTextFieldProps?.InputProps || {}),
      startAdornment: null, 
    },

    sx: {
      fontSize: "12px",
      borderRadius: "6px",
      minWidth: "100px",
      maxWidth: "150px",
      ...muiSearchTextFieldProps?.sx,
    },
  }}
        muiPaginationProps={{
          ...muiPaginationProps,
          rowsPerPageOptions: [3, 5, 10, 15],
          showFirstButton: false,
          showLastButton: false,
          sx: {
            "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows, & .MuiSelect-select, & .MuiInputBase-root, & .MuiTypography-root":
              {
                fontSize: "11px !important",
              },
            "& .MuiInputLabel-root": {
              fontSize: "11px !important",
            },
            ...muiPaginationProps?.sx,
          },
        }}
        renderEmptyRowsFallback={
          renderEmptyRowsFallback ||
          (() => (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flex: 1,
                height: "100%",
                minHeight: "150px",
                position: emptyStateAlign === "left" ? "sticky" : "static",
                left: emptyStateAlign === "left" ? "50%" : "auto",
                transform:
                  emptyStateAlign === "left" ? "translateX(-50%)" : "none",
                width: emptyStateAlign === "left" ? "fit-content" : "100%",
              }}
            >
              <Box
                component="span"
                sx={{
                  display: "block",
                  textAlign: "center !important",
                  width: "100%",
                  fontSize: "12px",
                  color: error ? "#d93025" : "#a3a2a2",
                  fontWeight: error ? 600 : 500,
                }}
              >
                {error ? errorMessage : "No Data Found"}
              </Box>
            </Box>
          ))
        }
      />
    );
  };
